import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import Tagify from "@yaireo/tagify/react";
import "@yaireo/tagify/dist/tagify.css";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import {
  createCountry,
  updateCountry
} from "../../store/slices/countryOptions";
import { showError } from "../../services/toast-service";
import ViewGalleryModal from "../gallery/view-gallery-modal";
import CreateGalleryModal from "../gallery/create-gallery-modal";
import { COUNTRY_MAP_TAG_NAME, THUMBNAIL_TYPE } from "../strings";
import { ISO_COUNTRY_CODES } from "./iso-country-codes";
import { buildCountryOptionSchema } from "./country-option-schema";

const emptyCountry = () => ({ isoCode: "", dialCode: "", digits: [] });

// Country names in German, e.g. "DE" -> "Deutschland"
const germanCountryNames = new Intl.DisplayNames(["de"], { type: "region" });
const countryChoices = ISO_COUNTRY_CODES.map((code) => ({
  code,
  name: germanCountryNames.of(code)
})).sort((a, b) => a.name.localeCompare(b.name, "de"));

// Whole numbers from 3 to 10; accepts text or a number
const isValidDigit = (value) => {
  const text = String(value).trim();
  const number = Number(text);
  return /^\d+$/.test(text) && number >= 3 && number <= 10;
};

// Adds a country option, or edits `option` when one is given. The key is never
// sent, so it cannot change.
const CountriesAddModal = ({ option, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { REACT_APP_ASSET_CDN_URL } = process.env;
  const [showGallery, setShowGallery] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const tags = useSelector((state) => state.tags);
  const countryMapTag = tags.tags.find(
    (tag) => tag.name === COUNTRY_MAP_TAG_NAME
  );
  const schema = useMemo(() => buildCountryOptionSchema(t), [t]);

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: option
      ? {
          name: option.name,
          countries: option.countries.map((country) => ({
            isoCode: country.isoCode,
            dialCode: country.dialCode,
            digits: country.postalCodeLengths
          })),
          mapImage: option.mapImageUrl ? { key: option.mapImageUrl } : null
        }
      : {
          name: "",
          countries: [emptyCountry()],
          mapImage: null
        }
  });
  const { fields, append } = useFieldArray({ control, name: "countries" });

  const onSubmit = async (data) => {
    try {
      const body = {
        name: data.name,
        countries: data.countries.map((country) => ({
          isoCode: country.isoCode,
          dialCode: country.dialCode,
          postalCodeLengths: country.digits
        })),
        mapImageUrl: data.mapImage.key
      };
      if (option) {
        await dispatch(updateCountry({ id: option._id, body })).unwrap();
      } else {
        await dispatch(createCountry(body)).unwrap();
      }
      onClose();
    } catch (error) {
      if (error?.status === 409) {
        setError("name", { message: t("countries.errors.nameTaken") });
        return;
      }
      showError(error?.message);
    }
  };

  return (
    <>
      <Modal show onHide={onClose} size="lg" centered>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Modal.Header closeButton>
            <Modal.Title>
              {option ? t("countries.editCountry") : t("countries.addCountry")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t("countries.columns.name")}</Form.Label>
              <Form.Control isInvalid={!!errors.name} {...register("name")} />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>

            {fields.map((field, index) => (
              <Row key={field.id} className="mb-3">
                <Col md={5}>
                  <Form.Label>{t("countries.columns.countries")}</Form.Label>
                  <Form.Select
                    isInvalid={!!errors.countries?.[index]?.isoCode}
                    {...register(`countries.${index}.isoCode`)}
                  >
                    <option value="" />
                    {countryChoices.map((choice) => (
                      <option key={choice.code} value={choice.code}>
                        {choice.name} ({choice.code})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.countries?.[index]?.isoCode?.message}
                  </Form.Control.Feedback>
                </Col>
                <Col md={3}>
                  <Form.Label>{t("countries.columns.dialCode")}</Form.Label>
                  <Form.Control
                    placeholder="+49"
                    isInvalid={!!errors.countries?.[index]?.dialCode}
                    {...register(`countries.${index}.dialCode`)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.countries?.[index]?.dialCode?.message}
                  </Form.Control.Feedback>
                </Col>
                <Col md={4}>
                  <Form.Label>
                    {t("countries.columns.postalCodeDigits")}
                  </Form.Label>
                  <Controller
                    name={`countries.${index}.digits`}
                    control={control}
                    render={({ field: digitsField }) => (
                      // Enter adds the number; the wrapper stops it from submitting the form

                      <Tagify
                        defaultValue={digitsField.value}
                        settings={{
                          placeholder: "5",
                          delimiters: ",",
                          duplicates: false,
                          dropdown: { enabled: 0 },
                          validate: (tagData) =>
                            isValidDigit(tagData.value) ||
                            t("countries.errors.digitsFormat")
                        }}
                        onChange={(event) =>
                          digitsField.onChange(
                            event.detail.tagify.value
                              .map((tag) => Number(tag.value))
                              .filter(isValidDigit)
                          )
                        }
                        onBlur={digitsField.onBlur}
                      />
                    )}
                  />
                  {errors.countries?.[index]?.digits && (
                    <div className="text-danger small mt-1">
                      {errors.countries[index].digits.message}
                    </div>
                  )}
                </Col>
              </Row>
            ))}
            <Button
              variant="link"
              className="p-0 mb-3"
              onClick={() => append(emptyCountry())}
            >
              {t("countries.addAnotherCountry")}
            </Button>

            <Form.Group className="mb-3">
              <Form.Label>{t("countries.columns.mapImage")}</Form.Label>
              <Controller
                name="mapImage"
                control={control}
                render={({ field }) => (
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowGallery(true)}
                    >
                      {t("countries.chooseMapImage")}
                    </Button>
                    {field.value && (
                      <img
                        className="d-block mt-3"
                        src={`${REACT_APP_ASSET_CDN_URL}/${field.value.key}/${THUMBNAIL_TYPE.ANSWER_OPTION_ICON}`}
                        alt={t("countries.columns.mapImage")}
                        width={120}
                      />
                    )}
                    {showGallery && (
                      <ViewGalleryModal
                        tagsSelected={countryMapTag ? [countryMapTag] : []}
                        selectImage={(asset) => {
                          field.onChange(asset);
                          setShowGallery(false);
                        }}
                        handleClose={() => setShowGallery(false)}
                        toggleAddImage={() => {
                          setShowGallery(false);
                          setShowUpload(true);
                        }}
                      />
                    )}
                    {showUpload && (
                      <CreateGalleryModal
                        tags={countryMapTag ? [countryMapTag] : []}
                        onFinish={(asset) => {
                          field.onChange(asset);
                          setShowUpload(false);
                        }}
                        onClose={() => setShowUpload(false)}
                      />
                    )}
                    {errors.mapImage && (
                      <div className="text-danger small mt-1">
                        {errors.mapImage.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {t("common.save")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default CountriesAddModal;
